import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Button, message, Table, Select, Upload,
  DatePicker, Modal, Drawer, Spin,
  InputNumber,
  Tag,
  Tooltip,
  Space
} from 'antd';
import {
  ExclamationCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import './index.css';
import apiClient from '../../utils/apiService';
import { extraFeeApi } from '../../api/finance';
import dayjs from 'dayjs';

const { Option } = Select;

function Finance() {
  const [messageApi, contextHolder] = message.useMessage();
  const [myModal, modalContextHolder] = Modal.useModal();

  // 主表
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [currentFilters, setCurrentFilters] = useState({});
  const [form] = Form.useForm();

  // Agent BI 抽屉
  const [agentDrawerVisible, setAgentDrawerVisible] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [agentSummary, setAgentSummary] = useState({});
  const [agentPagination, setAgentPagination] = useState({ current: 1, pageSize: 100, total: 0 });
  const [currentReportDate, setCurrentReportDate] = useState('');
  const [currentIsAll, setCurrentIsAll] = useState(0); // 新增：当前记录的 isAll 值
  const [agentEditMap, setAgentEditMap] = useState({});
  const [agentUpdatingIds, setAgentUpdatingIds] = useState({});

  // HBL BI 抽屉
  const [hblDrawerVisible, setHblDrawerVisible] = useState(false);
  const [hblLoading, setHblLoading] = useState(false);
  const [hblData, setHblData] = useState([]);
  const [hblSummary, setHblSummary] = useState({});
  const [hblPagination, setHblPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [currentHblReportDate, setCurrentHblReportDate] = useState('');
  const [hblEditMap, setHblEditMap] = useState({});
  const [hblUpdatingIds, setHblUpdatingIds] = useState({});
  const [hblAgentFilter, setHblAgentFilter] = useState('');
  const [hblSourceFlagFilter, setHblSourceFlagFilter] = useState('');

  // Edit Modal
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [editForm] = Form.useForm();

  // Import Modal
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importReportDate, setImportReportDate] = useState(null);


  // Refresh Modal
  const [refreshModalVisible, setRefreshModalVisible] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshMode, setRefreshMode] = useState('MONTHLY');
  const [refreshForm] = Form.useForm();

  // 加载主表
  const loadData = useCallback(async (page = 1, pageSize = 10, filterParams = {}) => {
    setLoading(true);
    try {
      const params = { page, size: pageSize, ...filterParams };
      const response = await apiClient.post('/finance/monthly-report/page', params);
      if (response.data?.code === 200) {
        const { records, total, page: p, size: s } = response.data.data;
        setTableData(records || []);
        setPagination({ current: p, pageSize: s, total });
      } else {
        messageApi.error(response.data?.message || 'Failed to load data');
      }
    } catch (err) {
      messageApi.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadData();
  }, []);


  // 筛选提交
  const onFinish = useCallback((values) => {
    const params = {};
    if (values.year) params.year = parseInt(values.year);
    if (values.month) params.month = parseInt(values.month);
    setCurrentFilters(params);
    loadData(1, pagination.pageSize, params);
  }, [loadData, pagination.pageSize]);



  // 加载 Agent BI
  const loadAgentBi = useCallback(async (reportDate, page = 1, pageSize = 100, isAll = 0) => {
    setAgentLoading(true);
    try {
      const response = await apiClient.post('/finance/monthly-report/agent-bi/page', {
        reportDate, page, size: pageSize, isAll,
      });
      if (response.data?.code === 200) {
        const { records, total, page: p, size: s, agentNums, totalBalance } = response.data.data;
        setAgentData(records || []);
        setAgentSummary({ agentNums, totalBalance });
        setAgentPagination({ current: p, pageSize: s, total });
        const initMap = {};
        (records || []).forEach(r => {
          initMap[r.id] = { creditLine: r.creditLine, settledUpTo: r.settledUpTo };
        });
        setAgentEditMap(prev => ({ ...prev, ...initMap }));
      } else {
        messageApi.error(response.data?.message || 'Failed to load agent BI');
      }
    } catch (err) {
      messageApi.error('Failed to load agent BI');
    } finally {
      setAgentLoading(false);
    }
  }, [messageApi]);

  // 更新 Agent BI 行
  const updateAgentRow = async (id) => {
    setAgentUpdatingIds(prev => ({ ...prev, [id]: true }));
    try {
      const row = agentEditMap[id] || {};
      const payload = { id, creditLine: row.creditLine, settledUpTo: row.settledUpTo };
      const response = await apiClient.put('/finance/monthly-report/agent-bi/update', payload);
      if (response.data?.code === 200) {
        messageApi.success('Updated successfully');
        loadAgentBi(currentReportDate, agentPagination.current, agentPagination.pageSize);
      } else {
        messageApi.error(response.data?.message || 'Update failed');
      }
    } catch (err) {
      messageApi.error('Update failed');
    } finally {
      setAgentUpdatingIds(prev => ({ ...prev, [id]: false }));
    }
  };

  // 加载 HBL BI
  const loadHblBi = useCallback(async (reportDate, page = 1, pageSize = 10, agent = '', isAll = 0, sourceFlag = '') => {
    setHblLoading(true);
    try {
      const params = {
        reportDate,
        page,
        size: pageSize,
        isAll,
      };
      
      if (agent) {
        params.agent = agent;
      }
      if (sourceFlag) {
        params.sourceFlag = sourceFlag;
      }
      
      const response = await apiClient.post('/finance/monthly-report/hbl-bi/page', params);
      if (response.data?.code === 200) {
        const { records, total, page: p, size: s, agentNums, hblAmount, totalBalance } = response.data.data;
        setHblData(records || []);
        setHblSummary({ agentNums, hblAmount, totalBalance });
        setHblPagination({ current: p, pageSize: s, total });
        const initMap = {};
        (records || []).forEach(r => {
          initMap[r.id] = {
            etd: r.etd,
            balanceToOrigin: r.balanceToOrigin,
            releaseDate: r.releaseDate,
            note: r.note,
          };
        });
        setHblEditMap(prev => ({ ...prev, ...initMap }));
      } else {
        messageApi.error(response.data?.message || 'Failed to load HBL BI');
      }
    } catch (err) {
      messageApi.error('Failed to load HBL BI');
    } finally {
      setHblLoading(false);
    }
  }, [messageApi]);


  // 打开 Details 抽屉（Agent BI）
  const openAgentDrawer = (record) => {
    if (!record.year || !record.month) {
      messageApi.warning('No valid report date');
      return;
    }
    const reportDate = `${record.year}-${String(record.month).padStart(2, '0')}`;
    setCurrentReportDate(reportDate);
    setCurrentIsAll(record.isAll || 0); // 保存 isAll
    setAgentDrawerVisible(true);
    loadAgentBi(reportDate, 1, 100, record.isAll || 0);
  };

  // 打开 Summary/View 抽屉（HBL BI）
   const openHblDrawer = (record) => {
    if (!record.year || !record.month) {
      messageApi.warning('No valid report date');
      return;
    }
    const reportDate = `${record.year}-${String(record.month).padStart(2, '0')}`;
    setCurrentHblReportDate(reportDate);
    setCurrentIsAll(record.isAll || 0); // 保存 isAll
    setHblDrawerVisible(true);
    setHblAgentFilter('');
    setHblSourceFlagFilter('');
    loadHblBi(reportDate, 1, 10, '', record.isAll || 0);
  };

  // HBL BI 搜索
  const handleHblSearch = () => {
    if (!currentHblReportDate) {
      messageApi.warning('No report date selected');
      return;
    }
    loadHblBi(currentHblReportDate, 1, hblPagination.pageSize, hblAgentFilter, currentIsAll, hblSourceFlagFilter);
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/finance/monthly-report/delete/${id}`);
      if (response.data?.code === 200) {
        messageApi.success('Deleted successfully');
        loadData(pagination.current, pagination.pageSize, currentFilters);
      } else {
        messageApi.error(response.data?.message || 'Delete failed');
      }
    } catch (err) {
      messageApi.error('Delete failed');
    }
  };

  // 打开编辑 Modal
  const openEditModal = (record) => {
    setCurrentRecord(record);
    editForm.setFieldsValue({
      totalReleased: record.totalReleased,
      totalOther: record.totalOther,
      allowAgentView: record.allowAgentView,
      allowAgentDate: record.allowAgentDate ? dayjs(record.allowAgentDate) : undefined,
    });
    setEditModalVisible(true);
  };

  // 提交编辑
  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const payload = {
        id: currentRecord.id,
        totalReleased: values.totalReleased !== undefined ? parseInt(values.totalReleased) : undefined,
        totalOther: values.totalOther !== undefined ? parseInt(values.totalOther) : undefined,
        allowAgentView: values.allowAgentView,
        allowAgentDate: values.allowAgentDate
          ? dayjs(values.allowAgentDate).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
      };
      const response = await apiClient.put('/finance/monthly-report/update', payload);
      if (response.data?.code === 200) {
        messageApi.success('Updated successfully');
        setEditModalVisible(false);
        loadData(pagination.current, pagination.pageSize, currentFilters);
      } else {
        messageApi.error(response.data?.message || 'Update failed');
      }
    } catch (err) {
      messageApi.error('Update failed');
    } finally {
      setEditLoading(false);
    }
  };

  // 更新 HBL BI 行
  const updateHblRow = async (id) => {
    setHblUpdatingIds(prev => ({ ...prev, [id]: true }));
    try {
      const row = hblEditMap[id] || {};
      const payload = {
        id,
        etd: row.etd,
        balanceToOrigin: row.balanceToOrigin,
        releaseDate: row.releaseDate,
        note: row.note,
      };
      const response = await apiClient.put('/finance/monthly-report/hbl-bi/update', payload);
      if (response.data?.code === 200) {
        messageApi.success('Updated successfully');
        loadHblBi(currentHblReportDate, hblPagination.current, hblPagination.pageSize);
      } else {
        messageApi.error(response.data?.message || 'Update failed');
      }
    } catch (err) {
      messageApi.error('Update failed');
    } finally {
      setHblUpdatingIds(prev => ({ ...prev, [id]: false }));
    }
  };

  // 导出 Agent BI
  const exportAgentBi = async () => {
    try {
      const response = await apiClient.post(
        '/finance/monthly-report/agent-bi/export',
        { reportDate: currentReportDate, page: 1, size: 10000, isAll: currentIsAll },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agent-bi-${currentReportDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      messageApi.error('Export failed');
    }
  };

  // 导出 HBL BI
  const exportHblBi = async () => {
    try {
      const params = {
        reportDate: currentHblReportDate,
        page: 1,
        size: 10000,
        isAll: currentIsAll,
      };
      
      if (hblAgentFilter) {
        params.agent = hblAgentFilter;
      }
      if (hblSourceFlagFilter) {
        params.sourceFlag = hblSourceFlagFilter;
      }
      
      const response = await apiClient.post(
        '/finance/monthly-report/hbl-bi/export',
        params,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hbl-bi-${currentHblReportDate}${hblAgentFilter ? '-' + hblAgentFilter : ''}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      messageApi.error('Export failed');
    }
  };

  // 主表列
  const columns = [
    {
      title: 'Report Month',
      dataIndex: 'reportMonth',
      key: 'reportMonth',
      render: (val) => val || 'Not Released',
    },
    {
      title: 'Type',
      dataIndex: 'isAll',
      key: 'isAll',
      width: 80,
      render: (val) => (
        val === 1 ? <Tag color="blue">No Release Date</Tag> : '-'
      ),
    },
    {
      title: 'Create Date',
      dataIndex: 'createDate',
      key: 'createDate',
    },
    {
      title: 'Total Released',
      dataIndex: 'totalReleased',
      key: 'totalReleased',
      align: 'right',
    },
    {
      title: 'Total Other',
      dataIndex: 'totalOther',
      key: 'totalOther',
      align: 'right',
    },
    {
      title: 'Update Date',
      dataIndex: 'updateDate',
      key: 'updateDate',
    },
    {
      title: 'Allow Agent View',
      dataIndex: 'allowAgentView',
      key: 'allowAgentView',
      render: (val) => val || '-',
    },
    {
      title: 'Allow Agent Date',
      dataIndex: 'allowAgentDate',
      key: 'allowAgentDate',
      render: (val) => val || '-',
    },
       {
      title: 'Operation',
      key: 'operation',
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Details">
            <Button type="default" size="small" icon={<EyeOutlined />} onClick={() => openHblDrawer(record)} />
          </Tooltip>
          <Tooltip title="Summary">
            <Button type="default" size="small" icon={<FileTextOutlined />} onClick={() => openAgentDrawer(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="default" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                myModal.confirm({
                  title: 'Confirm Delete',
                  icon: <ExclamationCircleOutlined />,
                  content: `Are you sure to delete report ${record.reportMonth || 'this record'}?`,
                  okText: 'Yes',
                  okType: 'danger',
                  cancelText: 'No',
                  onOk: () => handleDelete(record.id),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];


  // Agent BI 列
  const agentSetField = (id, field, value) =>
    setAgentEditMap(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const agentColumns = [
    { title: 'Agent', dataIndex: 'agent', key: 'agent' },
    { title: 'HBL Nums', dataIndex: 'hblNums', key: 'hblNums', align: 'right' },
    { title: 'Total Balance', dataIndex: 'totalBalance', key: 'totalBalance', align: 'right' },
    {
      title: 'Credit Line', dataIndex: 'creditLine', key: 'creditLine', width: 130,
      render: (val, record) => (
        <InputNumber
          size="small"
          value={agentEditMap[record.id]?.creditLine ?? val ?? ''}
          onChange={v => agentSetField(record.id, 'creditLine', v)}
          precision={2}
          step={0.01}
          controls={false}
        />
      ),
    },
    {
      title: 'Settled Up To', dataIndex: 'settledUpTo', key: 'settledUpTo', width: 150,
      render: (val, record) => (
        <DatePicker
          size="small"
          format="MM/DD/YYYY"
          value={agentEditMap[record.id]?.settledUpTo ? dayjs(agentEditMap[record.id].settledUpTo, 'MM/DD/YYYY') : (val ? dayjs(val, 'MM/DD/YYYY') : null)}
          onChange={(_, dateStr) => agentSetField(record.id, 'settledUpTo', dateStr)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Action', key: 'action', width: 80, fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary" size="small"
          loading={!!agentUpdatingIds[record.id]}
          onClick={() => updateAgentRow(record.id)}
        >Update</Button>
      ),
    },
  ];

  // HBL BI 列
  const hblSetField = (id, field, value) =>
    setHblEditMap(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const hblColumns = [
    { title: 'HBL No', dataIndex: 'hblNo', key: 'hblNo' },
    { title: 'MBL No', dataIndex: 'mblNo', key: 'mblNo' },
    {
      title: 'ETD', dataIndex: 'etd', key: 'etd', width: 120,
      render: (val, record) => (
        <Input size="small" value={hblEditMap[record.id]?.etd ?? val ?? ''}
          onChange={e => hblSetField(record.id, 'etd', e.target.value)} />
      ),
    },
    {
      title: 'Balance To Origin', dataIndex: 'balanceToOrigin', key: 'balanceToOrigin', width: 140,
      render: (val, record) => (
        <Input size="small" value={hblEditMap[record.id]?.balanceToOrigin ?? val ?? ''}
          onChange={e => hblSetField(record.id, 'balanceToOrigin', e.target.value)} />
      ),
    },
    {
      title: 'Release Date', dataIndex: 'releaseDate', key: 'releaseDate', width: 130,
      render: (val, record) => (
        <Input size="small" placeholder="MM/dd/yyyy"
          value={hblEditMap[record.id]?.releaseDate ?? val ?? ''}
          onChange={e => hblSetField(record.id, 'releaseDate', e.target.value)} />
      ),
    },
    {
      title: 'Note', dataIndex: 'note', key: 'note', width: 160,
      render: (val, record) => (
        <Input size="small" value={hblEditMap[record.id]?.note ?? val ?? ''}
          onChange={e => hblSetField(record.id, 'note', e.target.value)} />
      ),
    },
    { title: 'Agent', dataIndex: 'agent', key: 'agent' },
    {
      title: 'Action', key: 'action', width: 80, fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary" size="small"
          loading={!!hblUpdatingIds[record.id]}
          onClick={() => updateHblRow(record.id)}
        >Update</Button>
      ),
    },
  ];

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const y = dayjs().year() - i;
    return <Option key={y} value={y}>{y}</Option>;
  });

  const monthOptions = Array.from({ length: 12 }, (_, i) => (
    <Option key={i + 1} value={i + 1}>{i + 1}</Option>
  ));

  return (
    <div className="finance-container">
      {contextHolder}
      {modalContextHolder}

      {/* 筛选区 */}
      <div className="finance-search">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="finance-filter-row">
            <Form.Item name="year" label="Report Year">
              <Select placeholder="Select Year" style={{ width: 140 }} allowClear>
                {yearOptions}
              </Select>
            </Form.Item>
            <Form.Item name="month" label="Report Month">
              <Select placeholder="Select Month" style={{ width: 140 }} allowClear>
                {monthOptions}
              </Select>
            </Form.Item>
          </div>
          <div className="finance-filter-buttons">
            <Button type="primary" htmlType="submit">Search</Button>
            <Button onClick={() => { form.resetFields(); setCurrentFilters({}); loadData(); }}>Reset</Button>
          </div>
        </Form>
      </div>

      {/* 主表 */}
      <div className="finance-table">
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 0 12px 0', gap: 10,  }}>
          {/* 导入 */}
          <Button icon={<DownloadOutlined />} onClick={() => setImportModalVisible(true)}>Import</Button>

          {/* 导出 */}
          <Button icon={<UploadOutlined/>} onClick={exportAgentBi}>Export</Button>


          {/* 刷新 */}
          <Button icon={<ReloadOutlined />} onClick={() => {
            setRefreshMode('MONTHLY');
            refreshForm.resetFields();
            setRefreshModalVisible(true);
          }}>Refresh</Button>




        </div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={tableData}
          rowKey="id"
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => loadData(page, pageSize, currentFilters),
          }}
        />
      </div>

      {/* Agent BI 抽屉 (Summary/View) */}
      <Drawer
        title={`Agent BI Summary — ${currentReportDate}`}
        open={agentDrawerVisible}
        onClose={() => setAgentDrawerVisible(false)}
        width={800}
        extra={
          <Button icon={<DownloadOutlined />} size="small" onClick={exportAgentBi}>Export</Button>
        }
      >
        <Spin spinning={agentLoading}>
          <div style={{ marginBottom: 16, display: 'flex', gap: 32, fontSize: 13 }}>
            <span>Agents: <strong>{agentSummary.agentNums ?? '-'}</strong></span>
            <span>Total Balance: <strong>{agentSummary.totalBalance ?? '-'}</strong></span>
          </div>
          <Table
            columns={agentColumns}
            dataSource={agentData}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            size="small"
            bordered
            pagination={{
              current: agentPagination.current,
              pageSize: agentPagination.pageSize,
              total: agentPagination.total,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: (page, pageSize) => loadAgentBi(currentReportDate, page, pageSize, currentIsAll),
            }}
          />
        </Spin>
      </Drawer>

      {/* HBL BI 抽屉 (Details) */}
      <Drawer
        title={`HBL BI Details — ${currentHblReportDate}`}
        open={hblDrawerVisible}
        onClose={() => setHblDrawerVisible(false)}
        width={1200}
        extra={
          <Button icon={<DownloadOutlined />} size="small" onClick={exportHblBi}>Export</Button>
        }
      >
        <Spin spinning={hblLoading}>
          {/* 筛选区域 */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              placeholder="Search by Agent"
              value={hblAgentFilter}
              onChange={(e) => setHblAgentFilter(e.target.value)}
              onPressEnter={handleHblSearch}
              style={{ width: 240 }}
              allowClear
            />
            <Select
              placeholder="Source Flag"
              value={hblSourceFlagFilter || undefined}
              onChange={(value) => setHblSourceFlagFilter(value)}
              style={{ width: 140 }}
              allowClear
            >
              <Option value="pms">pms</Option>
              <Option value="pai">pai</Option>
              <Option value="primeone">primeone</Option>
            </Select>
            <Button type="primary" onClick={handleHblSearch}>
              Search
            </Button>
            <Button 
              onClick={() => {
                setHblAgentFilter('');
                setHblSourceFlagFilter('');
                loadHblBi(currentHblReportDate, 1, hblPagination.pageSize, '', currentIsAll, '');
              }}
            >
              Reset
            </Button>
          </div>

          {/* 汇总信息 */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 32, fontSize: 13 }}>
            <span>Agents: <strong>{hblSummary.agentNums ?? '-'}</strong></span>
            <span>No. of Shipments: <strong>{hblSummary.hblAmount ?? '-'}</strong></span>
            <span>Total Balance: <strong>{hblSummary.totalBalance ?? '-'}</strong></span>
          </div>

          <Table
            columns={hblColumns}
            dataSource={hblData}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            size="small"
            bordered
            pagination={{
              current: hblPagination.current,
              pageSize: hblPagination.pageSize,
              total: hblPagination.total,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: (page, pageSize) => loadHblBi(currentHblReportDate, page, pageSize, hblAgentFilter, currentIsAll, hblSourceFlagFilter),
            }}
          />
        </Spin>
      </Drawer>

      {/* Edit Modal */}
      <Modal
        title="Edit Monthly Report"
        open={editModalVisible}
        confirmLoading={editLoading}
        onCancel={() => { setEditModalVisible(false); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        okText="Update"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit} style={{ marginTop: 16 }}>
          <Form.Item name="totalReleased" label="Total Released">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="totalOther" label="Total Other">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="allowAgentView" label="Allow Agent View">
            <Select>
              <Option value="Y">Y — Allow</Option>
              <Option value="N">N — Not Allow</Option>
            </Select>
          </Form.Item>
          <Form.Item name="allowAgentDate" label="Allow Agent Date">
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import Extra Fee"
        open={importModalVisible}
        onCancel={() => { setImportModalVisible(false); setImportReportDate(null); }}
        footer={null}
        width={480}
      >
        <div style={{ padding: '16px 0' }}>
          {/* 模板下载 */}
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <a
              href="https://pai-ocr-files.oss-cn-beijing.aliyuncs.com/uploads/2026/03/18/aaefa7c05d874a7bb06011a9023fb58d.xlsx"
              download
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 24px',
                backgroundColor: '#f0f5ff',
                border: '1px dashed #1f63fb',
                borderRadius: 8,
                color: '#1f63fb',
                fontSize: 14,
                textDecoration: 'none',
              }}
            >
              <FileExcelOutlined style={{ fontSize: 24 }} />
              <span>Download Template</span>
            </a>
          </div>

          {/* 报告月份选择 */}
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8, color: '#ff4d4f' }}>*</span>
            <span style={{ marginRight: 8 }}>Report Date:</span>
            <DatePicker
              picker="month"
              value={importReportDate}
              onChange={(date) => setImportReportDate(date)}
              format="YYYY-MM"
              placeholder="Select month"
              style={{ width: 200 }}
            />
          </div>

          {/* 上传区域 */}
          <Upload.Dragger
            accept=".xls,.xlsx"
            showUploadList={false}
            disabled={!importReportDate}
            customRequest={async ({ file, onSuccess, onError }) => {
              if (!importReportDate) {
                messageApi.error('Please select a report month first');
                return;
              }
              setImportLoading(true);
              const formData = new FormData();
              formData.append('file', file);
              try {
                const response = await extraFeeApi.import(formData, importReportDate.format('YYYY-MM'));
                if (response.data?.code === 200) {
                  messageApi.success(response.data.message || 'Import submitted, processing in background');
                  onSuccess(response.data);
                  setImportModalVisible(false);
                  setImportReportDate(null);
                } else {
                  messageApi.error(response.data?.message || 'Import failed');
                  onError(new Error(response.data?.message));
                }
              } catch (err) {
                messageApi.error('Import failed');
                onError(err);
              } finally {
                setImportLoading(false);
              }
            }}
          >
            <div style={{ padding: '24px 0' }}>
              <UploadOutlined style={{ fontSize: 48, color: '#1f63fb' }} />
              <p style={{ marginTop: 16, marginBottom: 8, fontSize: 16, color: '#262626' }}>
                Click or drag file to this area to upload
              </p>
              <p style={{ fontSize: 14, color: '#8c8c8c' }}>
                Support for .xls, .xlsx files
              </p>
            </div>
          </Upload.Dragger>
        </div>
      </Modal>




      {/* Refresh Modal */}
      <Modal
        title="Refresh BI Data"
        open={refreshModalVisible}
        confirmLoading={refreshLoading}
        onCancel={() => {
          setRefreshModalVisible(false);
          refreshForm.resetFields();
        }}
        onOk={() => refreshForm.submit()}
        okText="Trigger"
        width={480}
      >
        <Form
          form={refreshForm}
          layout="vertical"
          onFinish={async (values) => {
            setRefreshLoading(true);
            try {
              const payload = { mode: refreshMode };
              if (refreshMode !== 'ALL') {
                payload.date = refreshMode === 'MONTHLY' 
                  ? values.date.format('YYYY-MM')
                  : values.date.format('YYYY-MM-DD');
              }
              const response = await apiClient.post('/finance/monthly-report/refresh/trigger', payload);
              if (response.data?.code === 200) {
                messageApi.success(response.data.message || 'Refresh task triggered successfully');
                setRefreshModalVisible(false);
                refreshForm.resetFields();
              } else {
                messageApi.error(response.data?.message || 'Failed to trigger refresh');
              }
            } catch (err) {
              messageApi.error('Failed to trigger refresh');
            } finally {
              setRefreshLoading(false);
            }
          }}
          style={{ marginTop: 16 }}
        >
          {/* 模式切换按钮 */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <Button
              type={refreshMode === 'MONTHLY' ? 'primary' : 'default'}
              onClick={() => {
                setRefreshMode('MONTHLY');
                refreshForm.resetFields();
              }}
              style={{ flex: 1 }}
            >
              MONTHLY
            </Button>
            <Button
              type={refreshMode === 'DAILY' ? 'primary' : 'default'}
              onClick={() => {
                setRefreshMode('DAILY');
                refreshForm.resetFields();
              }}
              style={{ flex: 1 }}
            >
              DAILY
            </Button>
            <Button
              type={refreshMode === 'ALL' ? 'primary' : 'default'}
              onClick={() => {
                setRefreshMode('ALL');
                refreshForm.resetFields();
              }}
              style={{ flex: 1 }}
            >
              ALL
            </Button>
          </div>

          {refreshMode === 'ALL' ? null : refreshMode === 'MONTHLY' ? (
            <Form.Item
              name="date"
              label="Month"
              rules={[{ required: true, message: 'Please select month' }]}
            >
              <DatePicker.MonthPicker
                style={{ width: '100%' }}
                format="YYYY-MM"
                placeholder="Select month (yyyy-MM)"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder="Select date (yyyy-MM-dd)"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

    </div>
  );
}

export default Finance;